import { z } from "zod";

export const noteColors = ["yellow", "green", "blue", "purple", "pink", "gray"] as const;
export const noteColorSchema = z.enum(noteColors);
export type NoteColor = z.infer<typeof noteColorSchema>;

export const notebookSchema = z.object({
  id: z.string(),
  name: z.string(),
  noteCount: z.number().int(),
});
export type Notebook = z.infer<typeof notebookSchema>;
export const notebookListSchema = z.object({ notebooks: z.array(notebookSchema) });
export type NotebookList = z.infer<typeof notebookListSchema>;
export const notebookInputSchema = z.object({ name: z.string().trim().min(1).max(200) });

const tagSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(50)
  .regex(/^[^,#\s][^,#]*$/, "Tags cannot contain commas or #");

/**
 * Field rules shared by create and patch; defaults only on create so a patch never erases what
 * the client did not send.
 */
const noteFields = {
  notebookId: z.uuid().nullable(),
  title: z.string().trim().max(500),
  body: z.string().max(200_000),
  pinned: z.boolean(),
  color: noteColorSchema.nullable(),
  tags: z
    .array(tagSchema)
    .max(20)
    .transform((tags) => [...new Set(tags)]),
  email: z.object({ id: z.string().min(1), subject: z.string().max(500) }).nullable(),
};

export const noteInputSchema = z.object({
  ...noteFields,
  notebookId: noteFields.notebookId.default(null),
  title: noteFields.title.default(""),
  body: noteFields.body.default(""),
  pinned: noteFields.pinned.default(false),
  color: noteFields.color.default(null),
  tags: noteFields.tags.default([]),
  email: noteFields.email.default(null),
});
export type NoteInput = z.infer<typeof noteInputSchema>;

export const notePatchSchema = z.object(noteFields).partial().extend({
  /**
   * The version the client edited. When someone else saved in between the API answers 409
   * instead of overwriting their words.
   */
  ifUpdatedAt: z.iso.datetime().optional(),
});
export type NotePatch = z.infer<typeof notePatchSchema>;

export const noteAccessSchema = z.enum(["owner", "edit", "view"]);
export type NoteAccess = z.infer<typeof noteAccessSchema>;
export const notePermissionSchema = z.enum(["view", "edit"]);
export type NotePermission = z.infer<typeof notePermissionSchema>;

export const noteShareSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  email: z.string(),
  permission: notePermissionSchema,
});
export type NoteShare = z.infer<typeof noteShareSchema>;
export const noteShareInputSchema = z.object({
  userId: z.uuid(),
  permission: notePermissionSchema,
});

export const noteSchema = z.object({
  id: z.string(),
  /** What the signed-in user may do with this note. */
  access: noteAccessSchema,
  /** The owner, when it is someone else. */
  owner: z.object({ id: z.string(), displayName: z.string() }).nullable(),
  /** Who else can open it; only the owner sees this list. */
  shares: z.array(noteShareSchema),
  notebookId: z.string().nullable(),
  title: z.string(),
  body: z.string(),
  pinned: z.boolean(),
  color: noteColorSchema.nullable(),
  tags: z.array(z.string()),
  email: z.object({ id: z.string(), subject: z.string() }).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Note = z.infer<typeof noteSchema>;

/** List entries carry a preview instead of the whole body. */
export const noteSummarySchema = noteSchema
  .omit({ body: true, shares: true })
  .extend({ preview: z.string(), shared: z.boolean() });
export type NoteSummary = z.infer<typeof noteSummarySchema>;
export const noteListSchema = z.object({ notes: z.array(noteSummarySchema) });
export type NoteList = z.infer<typeof noteListSchema>;

export const noteQuerySchema = z.object({
  notebookId: z.uuid().optional(),
  /** Only notes colleagues shared with the signed-in user. */
  shared: z.enum(["1"]).optional(),
  tag: z.string().trim().toLowerCase().max(50).optional(),
  q: z.string().trim().max(200).optional(),
});
export type NoteQuery = z.infer<typeof noteQuerySchema>;

export const noteTagListSchema = z.object({
  tags: z.array(z.object({ tag: z.string(), count: z.number().int() })),
});
export type NoteTagList = z.infer<typeof noteTagListSchema>;

/** Colleagues in the same workspace, for picking whom to share with. */
export const personSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  email: z.string(),
});
export type Person = z.infer<typeof personSchema>;
export const peopleSchema = z.object({ people: z.array(personSchema) });
export const peopleQuerySchema = z.object({ q: z.string().trim().max(100).optional() });
